import { useList, useDelete, useUpdate } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  StatusBadge,
  getExpiryStatus,
} from '@/components/shared';
import {
  DataTable,
  TableToolbar,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  MousePointerClick,
  TrendingUp,
  ArrowUpDown,
  ImageIcon,
  Users,
} from 'lucide-react';
import { ReorderModal } from '@/components/modals';
import { notify } from '@/lib/notify';
import { type Banner, getBannerBackgroundUrl, calculateCTR } from './types';
import { BannerEventsDialog, type EventView } from './BannerEventsDialog';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

/**
 * Format number with K/M suffix
 */
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * Banners list page with DataTable
 */
export function BannersList() {
  const navigate = useNavigate();
  useScrollRestoration();
  const [page, setPage] = usePersistedState('banners:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('banners:sort', { field: 'order', order: 'asc' });
  const [search, setSearch] = usePersistedState('banners:search', '');
  const [activeFilter, setActiveFilter] = usePersistedState<string>('banners:activeFilter', 'all');
  const [deleteItems, setDeleteItems] = useState<Banner[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [eventsDialog, setEventsDialog] = useState<{ banner: Banner; type: EventView } | null>(null);
  const pageSize = 20;

  const { result, query } = useList<Banner>({
    resource: 'banners',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: [
      ...(activeFilter !== 'all'
        ? [
            {
              field: 'isActive',
              operator: 'eq' as const,
              value: activeFilter === 'true',
            },
          ]
        : []),
      ...(search
        ? [{ field: 'search', operator: 'contains' as const, value: search }]
        : []),
    ],
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const { mutate: deleteBanner } = useDelete();
  const { mutate: updateBanner } = useUpdate();

  const banners = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;

  const handleToggleActive = (items: Banner[]) => {
    if (items.length === 0) {return;}
    items.forEach((banner) => {
      updateBanner({
        resource: 'banners',
        id: banner.id,
        values: { isActive: !banner.isActive },
        successNotification: false,
      });
    });
    const activated = items[0].isActive === false;
    notify.success(
      activated ? `Активировано: ${items.length}` : `Деактивировано: ${items.length}`,
    );
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    for (const item of deleteItems) {
      deleteBanner({
        resource: 'banners',
        id: item.id,
        successNotification: false,
      });
    }
    notify.success(`Удалено: ${deleteItems.length}`);
    setDeleteItems([]);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) {return '—';}
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns: Column<Banner>[] = [
    {
      key: 'images',
      header: 'Баннер',
      render: (_, banner) => {
        const bgUrl = getBannerBackgroundUrl(banner);
        return (
          <div className="flex items-center gap-3">
            {bgUrl ? (
              <img
                src={bgUrl}
                alt={`Баннер #${banner.id}`}
                className="h-12 w-24 object-cover rounded"
              />
            ) : (
              <div className="h-12 w-24 bg-muted rounded flex items-center justify-center">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate max-w-[200px]">
                Баннер #{banner.id}
              </p>
              <p className="text-sm text-muted-foreground">
                {!banner.startDate && !banner.endDate
                  ? 'Бессрочно'
                  : `${banner.startDate ? formatDate(banner.startDate) : '...'} — ${banner.endDate ? formatDate(banner.endDate) : 'Бессрочно'}`}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'imagesCount',
      header: 'Изобр.',
      width: '80px',
      hiddenOnMobile: true,
      render: (_, banner) => (
        <span className="text-sm text-muted-foreground">
          {banner.images.length}
        </span>
      ),
    },
    {
      key: 'stats',
      header: 'Статистика',
      width: '300px',
      hiddenOnMobile: true,
      render: (_, banner) => (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Просмотры (всего)"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'views' });
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            {formatNumber(banner.viewsCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные просмотры"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'unique-views' });
            }}
          >
            <Users className="h-3.5 w-3.5" />
            {formatNumber(banner.uniqueViewsCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Клики (всего)"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'clicks' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            {formatNumber(banner.clicksCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные клики"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'unique-clicks' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            <Users className="h-3 w-3 -ml-0.5" />
            {formatNumber(banner.uniqueClicksCount || 0)}
          </button>
          <span className="flex items-center gap-1" title="CTR">
            <TrendingUp className="h-3.5 w-3.5" />
            {calculateCTR(banner).toFixed(1)}%
          </span>
        </div>
      ),
    },
    {
      key: 'linkUrl',
      header: 'Ссылка',
      width: '80px',
      hiddenOnMobile: true,
      render: (_, banner) =>
        banner.linkUrl ? (
          <a
            href={banner.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '140px',
      render: (_, banner) => {
        if (!banner.isActive) {
          return <StatusBadge status="disabled" customLabel="Выключен" size="sm" />;
        }
        const expiryStatus = getExpiryStatus(
          banner.startDate ? new Date(banner.startDate) : null,
          banner.endDate ? new Date(banner.endDate) : null,
        );
        if (expiryStatus === 'expired') {
          return <StatusBadge status="expired" size="sm" />;
        }
        if (expiryStatus === 'scheduled') {
          return <StatusBadge status="scheduled" size="sm" />;
        }
        return <StatusBadge status="active" size="sm" />;
      },
    },
  ];

  const bulkActions: BulkAction<Banner>[] = [
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          navigate(`/banners/edit/${items[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Активировать',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter((i) => !i.isActive)),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter((i) => i.isActive)),
      variant: 'outline',
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  const filtersCount = activeFilter !== 'all' ? 1 : 0;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Баннеры"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setReorderOpen(true)}>
              <ArrowUpDown className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Порядок</span>
            </Button>
            <Button size="sm" onClick={() => navigate('/banners/create')}>
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Добавить баннер</span>
            </Button>
          </div>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по ID..."
        filters={
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="banner-active">Статус активности</Label>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger id="banner-active">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="true">Активные</SelectItem>
                  <SelectItem value="false">Неактивные</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        filtersCount={filtersCount}
        onResetFilters={() => {
          setSearch('');
          setActiveFilter('all');
        }}
      />

      <DataTable
        columns={columns}
        data={banners}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(banner) => navigate(`/banners/edit/${banner.id}`)}
        emptyMessage="Баннеры не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? 'Удалить баннер?'
                : `Удалить ${deleteItems.length} баннеров?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить баннер #{deleteItems[0].id}?
                  Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} баннеров?
                  Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BannerEventsDialog
        banner={eventsDialog?.banner ?? null}
        type={eventsDialog?.type ?? null}
        open={eventsDialog !== null}
        onOpenChange={(open) => {
          if (!open) {setEventsDialog(null);}
        }}
      />

      <ReorderModal
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        resource="banners"
        items={banners}
        title="Порядок баннеров"
        renderItem={(banner) => {
          const bgUrl = getBannerBackgroundUrl(banner);
          return (
            <div className="flex items-center gap-3">
              {bgUrl ? (
                <img
                  src={bgUrl}
                  alt=""
                  className="h-10 w-16 object-cover rounded"
                />
              ) : (
                <div className="h-10 w-16 bg-muted rounded flex items-center justify-center">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">Баннер #{banner.id}</p>
                <p className="text-xs text-muted-foreground">
                  {banner.images.length} изобр.
                </p>
              </div>
            </div>
          );
        }}
        onSuccess={() => query.refetch()}
      />
    </div>
  );
}
